import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { coreLoginClient } from 'app/api/apiClient/generated/coreLoginClient';
import { coreOAuthClient } from 'app/api/apiClient/generated/coreOAuthClient';
import { coreUsersClient } from 'app/api/apiClient/generated/coreUsersClient';
import {
  CaptchaRequest,
  CompanyDto,
  CoreLoginOktaLoginTypeParams,
  CoreOAuthGetInfoParams,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  SuccessResponse,
  UpdateMfaTypeRequest,
  UpgradeOktaTokenRequest,
} from 'app/api/apiClient/generated/types';
import {
  clearAlertMessage,
  clearSessionCurrencyTypeId,
  clearVerificationCodeCookie,
  getTokenExp,
  getTokenExpFromResponse,
  removeCompanyId,
  removeTokenExp,
  setAlertMessage,
  setRememberMe,
  setTokenExp,
} from 'app/api/cookie';
import { keys } from 'app/config/keys';
import { queries } from 'app/constants/queries';
import { routes } from 'app/constants/routes';
import { UserRoles } from 'app/constants/user';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import { PayorTypeIds } from 'app/pages/admin/Payors/Edit/constants';
import { gaSet } from 'app/services/google-analytics';
import { removeOktaTokens } from 'app/services/okta';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { useAuthDispatch, useAuthState, useIsInAnyRole } from 'app/state/AuthContext/hooks';
import { indexBy } from 'app/utils/lib/array';
import { handleErrors, handleSuccess } from 'app/utils/lib/context';
import { nowInSeconds } from 'app/utils/lib/dates';
import { removeCsrf, setCsrf } from 'app/utils/lib/dom';
import { isExternalVendor, isFactor, isPayee, isPayor, isPayorAdmin, isPayorSubmission } from 'app/utils/lib/user';

import { CoreApiError } from '../errors';

// Local Variables
export const invalidateAuthUser = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries([queries.authUser]);
};

export const useQueryUser = () => {
  const { tokenData } = useAuthState();
  const isTokenExpired = useGetIsTokenExpired();

  return useQueryEnhanced(
    [queries.authUser],
    coreLoginClient.currentUser,
    {
      enabled: !!tokenData || !isTokenExpired,
      onSuccess: data => {
        if (data.user.id) {
          gaSet({ userId: data.user.id });
        }
      },
    },
  );
};

export const useGetUser = () => {
  const { data } = useQueryUser();

  return data?.user ?? null;
};

export const useGetTokenExpiration = () => {
  const { data, isFetched } = useQueryEnhanced(
    [queries.authTokenExpiration],
    coreLoginClient.tokenExpiration,
    {
      onSuccess: successData => {
        setTokenExp(getTokenExpFromResponse(successData));
        if (!successData.csrfToken) {
          removeCsrf();
        } else {
          setCsrf(successData.csrfToken);
        }
      },
    },
  );

  return {
    isFetched,
    tokenExp: getTokenExp() || getTokenExpFromResponse(data),
  };
};

export const useGetIsTokenExpired = () => {
  const { tokenData } = useAuthState();
  const { tokenExp } = useGetTokenExpiration();

  return tokenData ? tokenExp < nowInSeconds() : true;
};

export const useGetOktaType = () => {
  return useMutationEnhanced(coreLoginClient.oktaLoginType);
};

export const useQueryOktaType = (queryParams: CoreLoginOktaLoginTypeParams) => {
  const queryFn = useCallback(() => coreLoginClient.oktaLoginType(queryParams),
    [queryParams]);

  return useQueryEnhanced([queries.oktaLoginType], queryFn);
};

export const useQueryOktaInfo = () => {
  const { tokenData } = useAuthState();
  const isTokenExpired = useGetIsTokenExpired();

  return useQueryEnhanced(
    [queries.oktaInfo],
    coreUsersClient.oktaInfo,
    {
      enabled: !!tokenData || !isTokenExpired,
    },
  );
};

export type CaptchaErrors = Record<keyof Partial<CaptchaRequest>, string[]>;

export const useResetCaptcha = () => {
  return useMutationEnhanced<AxiosResponse<SuccessResponse>, CoreApiError<CaptchaErrors>, CaptchaRequest>(
    coreLoginClient.resetCaptcha,
  );
};

export const useExchangeToken = () => {
  const dispatch = useAuthDispatch();

  return useMutationEnhanced(coreLoginClient.newToken,
    {
      onError: () => {
        window.location.reload();
      },
      onSuccess: data => {
        dispatch({
          payload: {
            token: data.token,
          },
          type: 'setLoginInfo',
        });
      },
    });
};

export type LocalLoginRequest = LoginRequest & { rememberMe?: boolean };
export type LoginErrors = Record<keyof Partial<LoginRequest>, string[]>;

export const useLogin = () => {
  const dispatch = useAuthDispatch();
  const queryClient = useQueryClient();

  // we're wrapping coreLoginClient.login so we don't have to provide all
  // the different parameters for each request, and so we can provide the
  // API key and secret here.
  const mutationFn = useCallback((payload: Partial<LocalLoginRequest>) =>
    coreLoginClient.login({
      captchaToken: null,
      confirmNewPassword: null,
      deviceId: null,
      mfaCode: null,
      mobileAppVersion: null,
      newPassword: null,
      password: null,
      signatureInCookie: false,
      token: null,
      tokenType: null,
      username: null,
      ...payload,
      apiKey: keys.API_KEY,
      apiSecret: keys.API_SECRET,
    }),
  []);

  return useMutationEnhanced<AxiosResponse<LoginResponse>, CoreApiError<LoginErrors>, Partial<LocalLoginRequest>>(
    mutationFn,
    {
      onSuccess: (data, variables) => {
        const { rememberMe = false, username } = variables;
        if (username) {
          setRememberMe(rememberMe,
            username);
        }

        // Set the token expiration manually before setting the authUser
        queryClient.setQueryData([queries.authTokenExpiration],
          { seconds: 1800 });
        queryClient.setQueryData([queries.authUser],
          { user: data.user });

        // invalidate this query so a new API request will be made,
        // which will update any CSRF token the API might provide.
        void queryClient.invalidateQueries([queries.authTokenExpiration]);

        if (data.alertMessage) {
          setAlertMessage(data.alertMessage);
        } else {
          clearAlertMessage();
        }

        dispatch({
          payload: {
            token: data.token,
          },
          type: 'setLoginInfo',
        });
      },
    },
  );
};

export const useLogout = (redirect: string = window.location.origin) => {
  const mutationFn = useCallback(() => {
    clearVerificationCodeCookie();
    removeCompanyId();
    clearSessionCurrencyTypeId();
    removeCsrf();

    return coreLoginClient.logout();
  },
  []);

  return useMutationEnhanced(mutationFn,
    {
      onSuccess: () => {
        removeTokenExp();
        removeOktaTokens();

        if (window) {
          // Prevents a known issue in Safari where a user can
          // click the back button after logging out and load cached data
          window.location.replace(redirect);
        }
      },
      retry: 1,
    });
};

export const useRegisterMFA = () => {
  const mutationFn = ({
    payload,
    type,
  }: {
    payload: UpdateMfaTypeRequest;
    type: number;
  }) => coreUsersClient.updateMfaType(type, payload);

  return useMutationEnhanced(mutationFn);
};

export const useSendMFA = () => {
  return useMutationEnhanced(coreUsersClient.sendMfa);
};

export const useRequestPasswordReset = () => {
  return useMutationEnhanced(coreLoginClient.requestPasswordReset);
};

export const useRequestAuthorizedPasswordReset = () => {
  return useMutationEnhanced(coreLoginClient.requestAuthorizedPasswordReset);
};

export const useRequestMultiFactorReset = () => {
  return useMutationEnhanced(coreLoginClient.requestMultiFactorReset);
};

export const useIsExternalVendor = () => {
  const user = useGetUser();

  return useMemo(() => isExternalVendor(user),
    [user]);
};

export const useIsFactor = () => {
  const user = useGetUser();

  return useMemo(() => isFactor(user),
    [user]);
};

export const useFactorDetails = () => {
  const user = useGetUser();

  return useMemo(() => user?.factorDetails, [user]);
};

export const useFactorGuidLookup = () => {
  const isFactorUser = useIsFactor();
  const user = useGetUser();

  return useMemo(() =>
    isFactorUser
      ? indexBy(user?.availableCompanies ?? [], company => company.id ?? '')
      : {},
  [user?.availableCompanies, isFactorUser]);
};
export const useIsPayee = () => {
  const user = useGetUser();

  return useMemo(() => isPayee(user),
    [user]);
};

export const useIsPayor = () => {
  const user = useGetUser();

  return useMemo(() => isPayor(user),
    [user]);
};

export const useIsPayorAdmin = () => {
  const user = useGetUser();

  return useMemo(() => isPayorAdmin(user),
    [user]);
};

export const useIsPayorSubmission = () => {
  const user = useGetUser();

  return useMemo(() => isPayorSubmission(user),
    [user]);
};

export const useIsSelectCarrier = () => {
  const user = useGetUser();

  return useMemo(() => !!user?.payeeDetails?.isRateAccepted,
    [user]);
};

export const usePayeeDetails = () => {
  const user = useGetUser();

  return useMemo(() => user?.payeeDetails,
    [user]);
};

export const usePayorDetails = () => {
  const user = useGetUser();

  return useMemo(() => user?.payorDetails, [user]);
};

export const useIsBroker = () => {
  const payorDetails = usePayorDetails();

  return useMemo(() => payorDetails?.payorTypeId === PayorTypeIds.FactoredBroker
    || payorDetails?.payorTypeId === PayorTypeIds.TriumphPayBroker,
  [payorDetails]);
};

export const useIsFactoredBroker = () => {
  const payorDetails = usePayorDetails();

  return useMemo(() => payorDetails?.payorTypeId === PayorTypeIds.FactoredBroker,
    [payorDetails]);
};

export const useIsShipper = () => {
  const payorDetails = usePayorDetails();

  return useMemo(() => payorDetails?.payorTypeId === PayorTypeIds.Shipper,
    [payorDetails]);
};

export const useIsHighwaySubscriber = () => {
  const payorDetails = usePayorDetails();
  const isCsr = useIsInAnyRole([UserRoles.Csr, UserRoles.SeniorClientServiceAgent]);

  return useMemo(() =>
    !isCsr && !!payorDetails?.isHighwaySubscriber,
  [isCsr, payorDetails?.isHighwaySubscriber]);
};

export const useResetPassword = () => {
  const alertsDispatch = useAlertsDispatch();
  const queryClient = useQueryClient();

  return useMutationEnhanced<
    AxiosResponse<SuccessResponse>,
    CoreApiError<Partial<ResetPasswordRequest>>,
    ResetPasswordRequest>(
    coreLoginClient.resetPassword,
    {
      onError: error => {
        handleErrors({
          dispatch: alertsDispatch,
          errorObject: error,
          expectedKeys: ['password', 'confirmPassword'],
        });
      },
      onSuccess: () => {
        handleSuccess({
          dispatch: alertsDispatch,
          successMessage: 'Password saved!',
        });

        void queryClient.invalidateQueries([queries.authUser]);
      },
    },
  );
};

export const useLookupRegistrationCode = () => {
  return useMutationEnhanced(coreLoginClient.registrationCode);
};

export const useForwardRegistration = () => {
  return useMutationEnhanced(coreLoginClient.forwardRegistration);
};

export const useSendUserCode = () =>
  useMutationEnhanced(coreUsersClient.sendUserCode);

export const useUpdateSession = () => {
  return useMutationEnhanced(coreLoginClient.updateSession);
};

export const useVerifyUserCode = () =>
  useMutationEnhanced(coreUsersClient.verifyUserCode);

export const useGetOAuthInfo = (queryParams: CoreOAuthGetInfoParams) => {
  const queryFn = useCallback(() =>
    coreOAuthClient.getInfo(queryParams),
  [queryParams]);

  return useQueryEnhanced([queries.authOAuthInfo, queryParams],
    queryFn);
};

export const useGenerateOAuthRedirect = () =>
  useMutationEnhanced(coreOAuthClient.generateRedirect);

interface LocalUpgradeOktaTokenRequest {
  isMobile: boolean | null;
  oktaClientId: null | string;
  oktaTenantId: null | number;
  oktaToken: string;
  signatureInCookie: boolean;
}

export const useUpgradeOktaToken = () => {
  const alertsDispatch = useAlertsDispatch();
  const navigate = useNavigate();

  // we're wrapping coreLoginClient.upgradeOktaToken so we can provide the
  // API key and secret here.
  const mutationFn = useCallback((payload: LocalUpgradeOktaTokenRequest) => {
    const updatedPayload: UpgradeOktaTokenRequest = {
      ...payload,
      apiKey: keys.API_KEY,
      apiSecret: keys.API_SECRET,
      isMobile: false,
    };

    return coreLoginClient.upgradeOktaToken(updatedPayload);
  }, []);

  return useMutationEnhanced<
    AxiosResponse<LoginResponse>,
    CoreApiError<Partial<LocalUpgradeOktaTokenRequest>>,
    LocalUpgradeOktaTokenRequest>(
    mutationFn,
    {
      onError: error => {
        // If the app responded 403, the user successfully authenticated but is not authorized to access TPay
        if (error.status === 403) {
          navigate(`/${routes.oktaError}`, {
            replace: true,
            state: { message: error.data },
          });
        } else {
          // Display other errors normally
          handleErrors({
            dispatch: alertsDispatch,
            errorObject: error,
            expectedKeys: [],
          });
        }
      },
      onSuccess: data => {
        if (data.alertMessage) {
          setAlertMessage(data.alertMessage);
        } else {
          clearAlertMessage();
        }
      },
    },
  );
};

export const useCanViewPayeePromotionalCampaigns = () => {
  const isUserPayee = useIsPayee();
  const canViewPromotionalCampaigns = useIsInAnyRole([UserRoles.PayeeAdmin, UserRoles.PayeeBilling]);

  return isUserPayee && canViewPromotionalCampaigns;
};

export const useNewRefreshToken = () =>
  useMutationEnhanced(coreLoginClient.newRefreshToken);

interface UserMultiCompanyDetails {
  availableCompanies: CompanyDto[];
  hasMultipleCompanies: boolean;
}

export const useMultiCompanyDetails = (): UserMultiCompanyDetails => {
  const user = useGetUser();

  return useMemo(() => {
    const companies = user?.availableCompanies ?? [];

    return {
      availableCompanies: companies,
      hasMultipleCompanies: companies.length > 0,
    };
  },
  [user]);
};
